import React, { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@mui/material'
import Box from '@mui/material/Box'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import { useNotificationContext } from 'shared/context/Notifications'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import sendMessageService from 'shared/services/sendMessageService'
import isAxiosError from 'shared/utils/helpers/isAxiosError'
import useLogin from 'shared/store/useLogin'

import { noInformationsWholesales, UserRoles } from 'shared/consts'
import OrderedTable from './components/OrderedTable'
import { en, pl, de } from './locales'

export default function OrderSummary() {
   const location = useLocation()
   const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
   const { user } = useLogin()
   const [searchParams] = useSearchParams()
   const { addNotification } = useNotificationContext()
   const state =
      searchParams.get('type') === 'BUY_NOW'
         ? (location.state as OrderSummaryType)
         : (location.state as BuyNowSummaryType)
   const { i18n, t } = useTranslation('ORDER_SUMMARY')
   i18n.addResourceBundle('pl-PL', 'ORDER_SUMMARY', pl)
   i18n.addResourceBundle('en-EN', 'ORDER_SUMMARY', en)
   i18n.addResourceBundle('de-DE', 'ORDER_SUMMARY', de)
   const navigate = useNavigate()
   const [isReported, setIsReported] = useState(false)
   const [isReporting, setIsReporting] = useState(false)

   function handleWholesale(uuid: string) {
      switch (uuid) {
         case '0399a415-1950-4600-9393-c4dea6efbfa8': {
            const csvData: string[][] = []

            if (Array.isArray(state)) {
               state.map((item) =>
                  item.product.map((product) => {
                     if (product.wholesaleUuid === uuid) {
                        return csvData.push([
                           product.wholesaleIndexWithSpecialChars ?? product.index,
                           product.requestedAmount.toString(),
                        ])
                     }
                     return null
                  })
               )
            } else {
               state.product.map((product) => {
                  if (product.wholesaleUuid === uuid) {
                     return csvData.push([
                        product.wholesaleIndexWithSpecialChars ?? product.index,
                        product.requestedAmount.toString(),
                     ])
                  }
                  return null
               })
            }

            const csvContent = csvData.map((row) => row.join(';')).join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
            const url = URL.createObjectURL(blob)

            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'inter_cars_order.csv')
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)

            window.open(`https://pl.e-cat.intercars.eu/pl/cart`, '_blank')

            return null
         }
         default:
            return null
      }
   }

   function handleReportProblem(orderUuid: string, cartUuid: string, product: OrderProductType[]) {
      setIsReporting(true)
      const date = new Date()
      sendMessageService({
         typeMessage: 'Problem with order',
         message: `Problem occured in order. uuid: ${orderUuid}, cartUuid: ${cartUuid}, userUuid: ${
            user.uuid
         }, date: ${date.toLocaleString()}
         ${product
            .filter((item) => item.status !== 'AVAILABLE')
            .map((item) => `${item.productUuid} | ${item.wholesaleName} | ${item.wholesaleErrorMessage} \n`)}
        `,
      })
         .then((res) => {
            if (res.status === 204) {
               addNotification('successSend', 'success')
               setIsReported(true)
            }
         })
         .catch((error) => {
            if (isAxiosError<ErrorType>(error)) {
               addNotification(error.response?.data.errors[0].code || 'failedSave', 'error')
            } else {
               addNotification('failedSend', 'error')
            }
         })
         .finally(() => setIsReporting(false))
   }

   function renderContent() {
      if (Array.isArray(state)) {
         return state.map((order, index) => {
            return (
               <>
                  {order.product.some((item) => item.wholesaleName === 'Motorro') ? (
                     <Typography fontWeight="bold" color="red">
                        {t('motorroProducts')}
                     </Typography>
                  ) : (
                     state.length > 1 && index !== 0 && <Typography fontWeight="bold">{t('otherProducts')}</Typography>
                  )}
                  <OrderedTable
                     formattedNetValue={order.formattedNetValue}
                     netDeliveryCost={order.netDeliveryCost}
                     grossDeliveryCost={order.grossDeliveryCost}
                     formattedGrossValue={order.formattedGrossValue}
                     data={order.product}
                  />
                  {order.product.some((item) => item.wholesaleName === 'Motorro') && (
                     <ContainedButton
                        sx={{ alignSelf: 'end', mx: '18%' }}
                        variant="contained"
                        onClick={() => window.open(order.paymentLink, '_blank')}
                     >
                        {t('payForOrder')}
                     </ContainedButton>
                  )}
                  {/* {Object.keys(noInformationsWholesales).map(
                     (key) =>
                        order.product.find((item) => item.wholesaleUuid === key) && (
                           <ContainedButton
                              onClick={() => handleWholesale(key)}
                              fullWidth={isMobile}
                              sx={{ alignSelf: 'end', mx: { lg: '18%', md: '9%', xs: '0' } }}
                              variant="contained"
                           >
                              {`Zaimportuj plik csv w katalogu ${noInformationsWholesales[key].name}`}
                           </ContainedButton>
                        )
                  )} */}
                  <Box
                     sx={{
                        display: 'flex',
                        gap: 2,
                        width: { lg: 'auto', md: 'auto', xs: '100%' },
                        justifyContent: 'center',
                     }}
                  >
                     {index === state.length - 1 && (
                        <ContainedButton
                           fullWidth={isMobile}
                           onClick={() => navigate('/dashboard')}
                           variant="contained"
                        >
                           {t('backToMain')}
                        </ContainedButton>
                     )}
                     {order.product.some((item) => item.status !== 'AVAILABLE' && item.status !== 'TOO_LOW_AMOUNT') &&
                        !isReported && (
                           <OutlinedButton
                              onClick={() => handleReportProblem(order.uuid, order.cartUuid, order.product)}
                              variant="outlined"
                              disabled={isReporting}
                           >
                              {t('reportOrder')}
                           </OutlinedButton>
                        )}
                  </Box>
               </>
            )
         })
      }
      return (
         <>
            <OrderedTable
               formattedNetValue={state.formattedNetValue}
               formattedGrossValue={state.formattedGrossValue}
               data={state.product}
            />
            {/* {Object.keys(noInformationsWholesales).map(
               (key) =>
                  state.product.find((item) => item.wholesaleUuid === key) && (
                     <ContainedButton
                        onClick={() => handleWholesale(key)}
                        fullWidth={isMobile}
                        sx={{ alignSelf: 'end', mx: { lg: '18%', md: '9%', xs: '0' } }}
                        variant="contained"
                        disabled={
                           user.roles.includes(UserRoles.DEMO) || user.roles.includes(UserRoles.DEMO_SUBSCRIPTION)
                        }
                     >
                        {t('finishOrderInService', {
                           wholesaleName: noInformationsWholesales[key].name,
                        })}
                     </ContainedButton>
                  )
            )} */}
            <Box sx={{ display: 'flex', width: { lg: 'auto', md: 'auto', xs: '100%' }, gap: 2 }}>
               <ContainedButton fullWidth={isMobile} onClick={() => navigate('/dashboard')} variant="contained">
                  {t('backToMain')}
               </ContainedButton>
               {state.product.some(
                  (item) => item.status === 'WHOLESALE_CONNECTION_PROBLEM' || item.status === 'NOT_AVAILABLE'
               ) &&
                  !isReported && (
                     <ContainedButton
                        onClick={() => handleReportProblem(state.uuid, state.cartUuid, state.product)}
                        sx={{ alignSelf: 'end' }}
                        variant="contained"
                        disabled={isReporting}
                     >
                        {t('reportOrder')}
                     </ContainedButton>
                  )}
            </Box>
         </>
      )
   }

   // useEffect(() => {
   //    if (!state) {
   //       navigate('/')
   //    }
   // }, [state, navigate])

   return state ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px', py: 10, width: '100%' }}>
         <Typography variant="h3" color="primary">
            {t('summary')}
         </Typography>
         {renderContent()}
      </Box>
   ) : (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
         <CircularProgress color="primary" />
      </Box>
   )
}
